import { useHttpClient } from "@/composables/UseHttpClient";
import { useMutation, useQuery } from "@tanstack/vue-query";
import type {
  TerritoriesAIApiV1BillingPlanGetRequest,
  TerritoriesAIApiV1BillingPlanGetResponse,
  TerritoriesAIApiV1CommonResponse
} from "@territoriesai/types";
import { createRandomBillingPlan } from "./mock/mock-data-generator";
import { computed, toValue, type Ref } from "vue";

export const useGetBillingPlans = () => {
  const { request } = useHttpClient<
    TerritoriesAIApiV1BillingPlanGetRequest,
    never
  >({
    method: "get"
  });

  return useQuery({
    queryKey: ["billing-plans"],
    queryFn: async () => {
      try {
        const { billingPlans } =
          await request<TerritoriesAIApiV1BillingPlanGetResponse>(
            { url: `/s/v1/billing-plan` },
            {
              mockData: () => ({
                status: "OK",
                billingPlans: [
                  createRandomBillingPlan({
                    id: "d731a367-b348-4054-9aac-57cfed7acd66",
                    groupId: "free",
                    planGroupName: "Score",
                    price: 0,
                    planNames: ["1,000 Credits (Free)"]
                  }),
                  createRandomBillingPlan({
                    groupId: "build",
                    planGroupName: "Build",
                    price: 250,
                    planNames: ["Build Monthly"]
                  }),
                  createRandomBillingPlan({
                    groupId: "build",
                    planGroupName: "Build",
                    price: 2500,
                    planNames: ["Build Yearly"]
                  }),
                  createRandomBillingPlan({
                    groupId: "build",
                    planGroupName: "Build",
                    price: 600,
                    planNames: ["Build Monthly"]
                  }),
                  createRandomBillingPlan({
                    groupId: "build",
                    planGroupName: "Build",
                    price: 7500,
                    planNames: ["Build Yearly"]
                  })
                ]
              })
            }
          );
        return billingPlans;
      } catch (error) {
        console.error((error as Error).message);
        throw error;
      }
    }
  });
};

export const useGetBillingPlanById = (id: Ref<string> | string) => {
  const { request } = useHttpClient<
    TerritoriesAIApiV1BillingPlanGetRequest,
    never
  >({
    method: "get"
  });

  return useQuery({
    enabled: computed(() => !!toValue(id)),
    queryKey: ["billing-plans", id],
    queryFn: async () => {
      try {
        const { billingPlans } =
          await request<TerritoriesAIApiV1BillingPlanGetResponse>(
            { url: `/s/v1/billing-plan/${toValue(id)}` },
            {
              mockData: () => ({
                status: "OK",
                billingPlans: [
                  createRandomBillingPlan({
                    groupId: "free",
                    planGroupName: "Score",
                    price: 0,
                    planNames: ["Free"]
                  })
                ]
              })
            }
          );
        return billingPlans[0];
      } catch (error) {
        console.error((error as Error).message);
        throw error;
      }
    }
  });
};

export const useGetPaymentMethod = () => {
  const { request } = useHttpClient<never, never>({
    method: "get"
  });

  return useQuery({
    queryKey: ["payment-method"],
    queryFn: async () => {
      try {
        const { paymentMethods } = await request<
          {
            paymentMethods: Array<{
              id: string;
              brand: string;
              last4Digits: string;
              expirationMonth: number;
              expirationYear: number;
              isDefault: boolean;
              isUsedInSubscription: boolean;
            }>;
          } & TerritoriesAIApiV1CommonResponse
        >(
          { url: `/s/v1/billing-plan/payment-method` },
          {
            mockData: () => ({
              status: "OK",
              paymentMethods: [
                {
                  id: "pm_card_visa",
                  isDefault: true,
                  brand: "Visa",
                  last4Digits: "4242",
                  expirationMonth: 12,
                  expirationYear: 2025,
                  isUsedInSubscription: true
                },
                {
                  id: "pm_card_visa",
                  isDefault: false,
                  brand: "Visa",
                  last4Digits: "4242",
                  expirationMonth: 12,
                  expirationYear: 2025,
                  isUsedInSubscription: false
                }
              ]
            })
          }
        );
        return paymentMethods;
      } catch (error) {
        console.error((error as Error).message);
        throw error;
      }
    }
  });
};

export const useCreatePaymentMethod = () => {
  const { request } = useHttpClient<never, never>({
    method: "post"
  });

  return useMutation({
    mutationKey: ["create-payment-method"],
    mutationFn: async () => {
      try {
        const { clientSecret } = await request<
          {
            clientSecret: string;
          } & TerritoriesAIApiV1CommonResponse
        >(
          { url: `/s/v1/billing-plan/payment-method` },
          {
            mockData: () => ({
              status: "OK",
              customerId: "cus_123456789",
              clientSecret: "test_client_secret"
            })
          }
        );
        return { clientSecret };
      } catch (error) {
        console.error((error as Error).message);
        throw error;
      }
    }
  });
};

export const useDeletePaymentMethod = () => {
  const { request } = useHttpClient<never, never>({
    method: "delete"
  });

  return useMutation({
    mutationKey: ["delete-payment-method"],
    mutationFn: async (id: string) => {
      try {
        return await request<TerritoriesAIApiV1CommonResponse>(
          { url: `/s/v1/billing-plan/payment-method/${id}` },
          {
            mockData: () => ({
              status: "OK"
            })
          }
        );
      } catch (error) {
        console.error((error as Error).message);
        throw error;
      }
    }
  });
};
